import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { EmphasisBlock, CalloutBox, ButtonNode } from 'screens/Admin/Blog/components/TipTapExtensions';
import { useUploadBlogImageMutation } from 'store/services/blog/blogApi';
import 'screens/Admin/Blog/components/TipTapEditor.css';
import './BroadcastEditor.css';

interface Props {
  content: any;
  onChange: (json: any, html: string) => void;
}

const BroadcastEditor: React.FC<Props> = ({ content, onChange }) => {
  const [uploadImage] = useUploadBlogImageMutation();
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [showButtonModal, setShowButtonModal] = useState(false);
  const [buttonUrl, setButtonUrl] = useState('');
  const [buttonText, setButtonText] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      Underline,
      Image,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'article-link',
        },
      }),
      Placeholder.configure({
        placeholder: 'Compose your broadcast message...',
      }),
      EmphasisBlock,
      CalloutBox,
      ButtonNode,
    ],
    content: content,
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      const html = editor.getHTML();
      onChange(json, html);
    },
    editorProps: {
      attributes: {
        class: 'tiptap-content',
      },
    },
  });

  useEffect(() => {
    if (editor && !editor.isDestroyed && content) {
      const currentContent = editor.getJSON();
      if (JSON.stringify(currentContent) !== JSON.stringify(content)) {
        editor.commands.setContent(content);
      }
    }
  }, [content, editor]);

  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      const json = editor.getJSON();
      const html = editor.getHTML();
      onChange(json, html);
    }
  }, [editor]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!editor) {
    return <div>Loading editor...</div>;
  }

  const handleImageUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (file) {
        try {
          const formData = new FormData();
          formData.append('image', file);
          const result = await uploadImage(formData).unwrap();
          editor.chain().focus().setImage({ src: result.data.url }).run();
        } catch (error) {
          console.error('Image upload failed:', error);
        }
      }
    };
    input.click();
  };

  const handleInsertLink = () => {
    const { selection } = editor.state;
    const selectedText = editor.state.doc.textBetween(selection.from, selection.to);

    if (selectedText) {
      setLinkText(selectedText);
      setLinkUrl('');
      setShowLinkModal(true);
    } else {
      const url = prompt('Enter URL:');
      if (url) {
        editor.chain().focus().setLink({ href: url }).run();
      }
    }
  };

  const confirmLink = () => {
    if (linkUrl) {
      if (linkText) {
        editor.chain().focus().insertContent(`<a href="${linkUrl}" class="article-link">${linkText}</a>`).run();
      } else {
        editor.chain().focus().setLink({ href: linkUrl }).run();
      }
    }
    setShowLinkModal(false);
    setLinkUrl('');
    setLinkText('');
  };

  const handleInsertButton = () => {
    setButtonUrl('');
    setButtonText('');
    setShowButtonModal(true);
  };

  const confirmButton = () => {
    if (buttonUrl && buttonText) {
      editor.chain().focus().setButton({
        href: buttonUrl,
        text: buttonText,
        target: '_blank',
      }).run();
    }
    setShowButtonModal(false);
  };

  return (
    <div className="tiptap-editor-wrapper">
      <div className="tiptap-toolbar">
        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
            title="Heading 2"
          >
            H2
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
            title="Heading 3"
          >
            H3
          </button>
        </div>

        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'is-active' : ''}
            title="Bold"
          >
            <strong>B</strong>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? 'is-active' : ''}
            title="Italic"
          >
            <em>I</em>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={editor.isActive('underline') ? 'is-active' : ''}
            title="Underline"
          >
            <u>U</u>
          </button>
        </div>

        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive('bulletList') ? 'is-active' : ''}
            title="Bullet List"
          >
            •List
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive('orderedList') ? 'is-active' : ''}
            title="Numbered List"
          >
            1.List
          </button>
        </div>

        <div className="toolbar-group">
          <button onClick={handleInsertLink} title="Insert Link">
            Link
          </button>
          <button onClick={handleImageUpload} title="Upload Image">
            Image
          </button>
        </div>

        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleEmphasisBlock().run()}
            className={editor.isActive('emphasisBlock') ? 'is-active' : ''}
            title="Emphasis Block"
          >
            Emphasis
          </button>
          <button
            onClick={() => editor.chain().focus().toggleCalloutBox().run()}
            className={editor.isActive('calloutBox') ? 'is-active' : ''}
            title="Callout Box"
          >
            Callout
          </button>
          <button onClick={handleInsertButton} title="Insert Button">
            Button
          </button>
        </div>
      </div>

      <EditorContent editor={editor} />

      <div className="editor-footer">
        <span className="character-count">
          {editor.storage.characterCount?.characters() || editor.state.doc.textContent.length} characters
        </span>
      </div>

      {showLinkModal && (
        <div className="modal-backdrop" onClick={() => setShowLinkModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Insert Link</h3>
            <input
              type="text"
              placeholder="Link text"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              autoFocus
            />
            <input
              type="url"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
            />
            <div className="modal-actions">
              <button onClick={() => setShowLinkModal(false)}>Cancel</button>
              <button onClick={confirmLink} className="primary">Insert</button>
            </div>
          </div>
        </div>
      )}

      {showButtonModal && (
        <div className="modal-backdrop" onClick={() => setShowButtonModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Insert Button</h3>
            <input
              type="text"
              placeholder="Button text"
              value={buttonText}
              onChange={(e) => setButtonText(e.target.value)}
              autoFocus
            />
            <input
              type="url"
              placeholder="https://example.com"
              value={buttonUrl}
              onChange={(e) => setButtonUrl(e.target.value)}
            />
            <div className="modal-actions">
              <button onClick={() => setShowButtonModal(false)}>Cancel</button>
              <button onClick={confirmButton} className="primary">Insert</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BroadcastEditor;
